import React, { type CSSProperties } from "react";
import { css } from "@linaria/core";
import { color, fonts } from "..";
import { spacings } from "../Theme";
import classNames from "classnames";
import Tooltip from "../Tooltip";
import Link from "../Link";
import PercentBar from "../PercentBar";

export interface Item {
  id: string;
  label: string;
  description?: string;
  color?: string;
  percent?: number | null;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  overlayOptions?: Record<string, any>;
  icons?: React.ReactNode;
  trailingContent?: React.ReactNode;
}

interface OverlayComponentOptions {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any;
}

// wrapper to convert options prop to individual props for the overlay component
export function withOverlayComponentOptions<P extends Item["overlayOptions"]>(
  Component: React.ComponentType<P>,
): React.ComponentType<OverlayComponentOptions> {
  const WrappedComponent: React.ComponentType<OverlayComponentOptions> = ({ options }) => (
    <Component {...options} />
  );

  WrappedComponent.displayName = `withOverlayComponentOptions(${
    Component.displayName || Component.name || "Component"
  })`;

  return WrappedComponent;
}

interface ItemSelectorProps<
  T extends Item,
  O extends React.ComponentType<OverlayComponentOptions> | undefined = undefined,
> {
  items: T[];
  isSelectedItem: (itemId: string) => boolean;
  selectedItemIds?: string[];
  toggleItem: ((itemId: string) => void) | null;
  setItem: (itemId: string) => void;
  showPercentBar?: boolean;
  showPercent?: boolean;
  OverlayComponent?: O;
  fullWidth?: boolean;
  enableKeyboardNavigation?: boolean;
}

function ItemSelector<T extends Item>({
  items,
  isSelectedItem,
  selectedItemIds,
  toggleItem,
  setItem,
  showPercent,
  showPercentBar = true,
  OverlayComponent,
  fullWidth = true,
  enableKeyboardNavigation = false,
}: ItemSelectorProps<T, React.ComponentType<OverlayComponentOptions>>) {
  const itemRefs = React.useRef(new Map<string, HTMLAnchorElement | null>());
  const pendingFocusItemIdRef = React.useRef<string | null>(null);

  const handleClick = React.useCallback(
    (itemId: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      if (event.altKey || event.shiftKey || event.ctrlKey || event.metaKey) {
        if (toggleItem !== null) toggleItem(itemId);
      } else {
        setItem(itemId);
      }
    },
    [setItem, toggleItem],
  );

  const orderedSelectedItemIds = React.useMemo(() => {
    const knownItemIds = new Set(items.map(item => item.id));
    const sourceItemIds = selectedItemIds ?? items.map(item => item.id).filter(isSelectedItem);
    const uniqueItemIds: string[] = [];
    const seenItemIds = new Set<string>();

    for (const itemId of sourceItemIds) {
      if (!knownItemIds.has(itemId) || seenItemIds.has(itemId)) continue;
      seenItemIds.add(itemId);
      uniqueItemIds.push(itemId);
    }

    return uniqueItemIds;
  }, [isSelectedItem, items, selectedItemIds]);

  const setItemAndFocus = React.useCallback(
    (itemId: string) => {
      pendingFocusItemIdRef.current = itemId;
      setItem(itemId);
    },
    [setItem],
  );

  React.useLayoutEffect(() => {
    const pendingFocusItemId = pendingFocusItemIdRef.current;
    if (pendingFocusItemId === null) return;

    const frameId = window.requestAnimationFrame(() => {
      const itemElement = itemRefs.current.get(pendingFocusItemId);
      itemElement?.focus({ preventScroll: true });
      itemElement?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
      if (pendingFocusItemIdRef.current === pendingFocusItemId) {
        pendingFocusItemIdRef.current = null;
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  });

  const setItemRef = React.useCallback(
    (itemId: string) => (node: HTMLAnchorElement | null) => {
      if (node === null) {
        itemRefs.current.delete(itemId);
        return;
      }

      itemRefs.current.set(itemId, node);
    },
    [],
  );

  const handleKeyDown = React.useCallback(
    (itemId: string) => (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (!enableKeyboardNavigation || !["ArrowDown", "ArrowUp"].includes(event.key)) return;

      event.preventDefault();
      event.stopPropagation();

      if (orderedSelectedItemIds.length > 1) {
        const firstSelectedItemId = orderedSelectedItemIds[0];
        if (firstSelectedItemId !== undefined) setItemAndFocus(firstSelectedItemId);
        return;
      }

      const currentIndex = items.findIndex(item => item.id === itemId);
      if (currentIndex < 0) return;

      const lastIndex = items.length - 1;
      const nextIndex =
        event.key === "ArrowUp"
          ? currentIndex > 0
            ? currentIndex - 1
            : lastIndex
          : currentIndex === lastIndex
            ? 0
            : currentIndex + 1;
      const nextItem = items[nextIndex];
      if (nextItem !== undefined) setItemAndFocus(nextItem.id);
    },
    [items, orderedSelectedItemIds, setItemAndFocus, enableKeyboardNavigation],
  );

  return React.useMemo(
    () => (
      <div className={wrapperCss}>
        <div className={itemSelectorCss}>
          {items.map(item => (
            <Link
              ref={setItemRef(item.id)}
              key={item.id}
              style={{ "--item-link-color": item.color ?? color.carbon900 } as CSSProperties}
              className={classNames(itemLink, {
                selected: isSelectedItem(item.id),
                withTrailingContent: Boolean(item.trailingContent),
              })}
              onClick={handleClick(item.id)}
              onKeyDown={handleKeyDown(item.id)}
            >
              {OverlayComponent ? (
                <div className={classNames(subItemWrapperCss, { fullWidth })}>
                  <div className={itemTopCss}>
                    <Tooltip
                      overlay={<OverlayComponent options={item.overlayOptions} />}
                      arrowWidth={0}
                      placement="right-start"
                      backgroundColor={color.hydrogen}
                      borderColor={color.hydrogen}
                      color={color.carbon900}
                    >
                      <div className={itemTooltipTargetCss}>
                        <span className={labelCss}>{item.label}</span>
                        {item.icons && <div className={iconsCss}>{item.icons}</div>}
                      </div>
                    </Tooltip>
                  </div>
                  {item.description && (
                    <div
                      className={classNames(descriptionCss, { selected: isSelectedItem(item.id) })}
                    >
                      {item.description}
                    </div>
                  )}
                  {item.percent !== undefined && showPercentBar && (
                    <PercentBar
                      percent={item.percent ?? 0}
                      color={item.color}
                      disabled={!isSelectedItem(item.id)}
                      showPercent={showPercent}
                    />
                  )}
                </div>
              ) : (
                <div className={classNames(subItemWrapperCss, { fullWidth })}>
                  <div className={itemTopCss}>
                    <span className={labelCss}>{item.label}</span>
                    {item.icons && <div className={iconsCss}>{item.icons}</div>}
                  </div>
                  {item.description && (
                    <div
                      className={classNames(descriptionCss, { selected: isSelectedItem(item.id) })}
                    >
                      {item.description}
                    </div>
                  )}
                  {item.percent !== undefined && showPercentBar && (
                    <PercentBar
                      percent={item.percent ?? 0}
                      color={item.color}
                      disabled={!isSelectedItem(item.id)}
                      showPercent={showPercent}
                    />
                  )}
                </div>
              )}
              {item.trailingContent && (
                <span className={trailingContentCss}>{item.trailingContent}</span>
              )}
            </Link>
          ))}
        </div>
      </div>
    ),
    [
      items,
      isSelectedItem,
      handleClick,
      handleKeyDown,
      OverlayComponent,
      setItemRef,
      showPercent,
      showPercentBar,
      fullWidth,
    ],
  );
}

const wrapperCss = css`
  height: 100%;
`;

const subItemWrapperCss = css`
  display: flex;
  flex-direction: column;
  min-width: 0;

  &.fullWidth {
    width: 100%;
  }
`;

const itemSelectorCss = css`
  margin-right: ${spacings.m};
  display: flex;
  flex-direction: column;
  gap: ${spacings.xs};
`;

const iconsCss = css`
  display: flex;
  gap: ${spacings.xxs};
  align-items: flex-end;
`;

const descriptionCss = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 284px;

  &.selected {
    font: ${fonts.body3SemiBold};
    color: ${color.carbon700};
  }
  &:not(.selected) {
    font: ${fonts.body3};
    color: ${color.carbon600};
  }
`;

const itemTopCss = css`
  position: relative;
  display: flex;
  gap: ${spacings.xxs};
  min-width: 0px;
  max-width: 324px;
  align-items: center;
`;

const itemTooltipTargetCss = css`
  display: flex;
  gap: ${spacings.xxs};
  min-width: 0;
  align-items: center;
`;

const itemLink = css`
  display: flex;
  white-space: nowrap;
  position: relative;
  padding-left: 20px;
  color: ${color.carbon600};
  font: ${fonts.body2};

  &.withTrailingContent {
    align-items: center;
    justify-content: space-between;
    gap: ${spacings.s};

    .${subItemWrapperCss} {
      flex: 1;
    }
  }

  &:focus-visible {
    outline: none;
    background-color: ${color.darkNitrogen50};
  }

  &:not(.selected) {
    color: ${color.carbon700};
    &:hover {
      color: ${color.carbon900};
      .${itemTopCss}::before {
        color: var(--item-link-color, ${color.carbon600});
        position: absolute;
        left: -20px;
        content: "\u2b95";
        opacity: 50%;
      }
      .${iconsCss} {
        opacity: 100%;
      }
    }
    .${iconsCss} {
      opacity: 50%;
    }
  }

  &.selected {
    font: ${fonts.body2SemiBold};
    color: ${color.carbon900};
    .${itemTopCss}::before {
      color: var(--item-link-color, ${color.carbon600});
      position: absolute;
      left: -20px;
      content: "\u2b95";
      width: 18px;
      height: 18px;
    }
  }
`;

const trailingContentCss = css`
  flex-shrink: 0;
  max-width: 45%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
  color: ${color.carbon700};
  font: ${fonts.body3};
`;

const labelCss = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 284px;
`;

export default ItemSelector;
